import { format, parse, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { PillsState } from "barte-design-system";
import { showToast } from "@/utils";
import { formatCnpjString, hasCnpjShape } from "./validateCnpj";

type GatewayItem = {
  provider: string;
  status: string;
  error?: unknown;       
  fraudAnalysis?: unknown;
};

type NormalizeResult = {
  data: GatewayItem[];
  errors: { index?: number; message: string; raw?: unknown }[];
};

export function getPeriodicity(type: string) {
  const Periodicity = {
    MONTHLY: "Mensal",
    SEMESTER: "Semestral",
    YEARLY: "Anual",
  };
  return Periodicity[type] || type;
}

export const getStatus = (status: string) => {
  const Status = {
    SCHEDULED: "Agendadas",
    CANCELED: "Canceladas",
    PAID: "Pago",
    PAID_MANUALLY: "Pago Manualmente",
    LATE: "Atrasado",
    ACTIVE: "Ativo",
    PENDING: "Pendente",
    AUTHORIZED: "Autorizado",
    FAILED: "Erro",
    INACTIVE: "Inativo",
    ABANDONED: "Desistência",
    DEFAULTER: "Inadimplente",
    CHARGEBACK: "Contestada",
    REFUND: "Estornada",
    DISPUTE: "Em disputa",
    REVENUE_ANTICIPATION: "Antecipada",
    CREDIT_ANTICIPATED:"Saldo Antecipado",
    CREDIT_CREDIT_CARD:"Pagamento autorizado",
    DEBIT_ANTICIPATED:"Taxas de antecipação",
    DEBIT_CREDIT_CARD:"Taxa Barte",
    CREDIT_REVERSAL :"Estorno",
    PRE_AUTHORIZED: "Pré Autorizado",
    success: "Estornado",
    processing: "Processando",
    failed: "falhou",
    KYC_REJECTED: "Erro",
    
  };
  return Status[status] || status;
};

export const getNextLetter = (letter: string) => {
  if (typeof letter !== 'string' || letter.length !== 1 || !/[a-zA-Z]/.test(letter)) {
    throw new Error("Input must be a single alphabetical character.");
  }
  if (letter === 'z') {
    return 'a';
  } else if (letter === 'Z') {
    return 'A';
  } else {
    return String.fromCharCode(letter.charCodeAt(0) + 1);
  }
}
export const getColorBadgeStatus = (status: string): PillsState => {
  const BadgeStatus: Record<string, PillsState> = {
    SCHEDULED: "alert",
    CANCELED: "error",
    PAID: "success",
    PAID_MANUALLY: "success",
    LATE: "alert",
    ACTIVE: "success",
    PENDING: "alert",
    AUTHORIZED: "info",
    CREATED: "info",
    FAILED: "error",
    INACTIVE: "default",
    ABANDONED: "accent",
    DEFAULTER: "accent",
    PROCESS_CANCEL: "error",
    SEND_INVOICE_SYSTEM: "alert",
    SEND_AUTHORIZED: "alert",
    PROCESS_AUTHORIZED: "alert",
    CHARGEBACK: "alert",
    REFUND: "alert",
    CHARGEBACK_LIST: "warning",
    DISPUTE: "error",
    BLOCKED: "error",
    REVENUE_ANTICIPATION: "alert",
    CREDIT_ANTICIPATED:"default",
    CREDIT_CREDIT_CARD:"success",
    DEBIT_ANTICIPATED:"error",
    DEBIT_CREDIT_CARD:"error",
    CREDIT_REVERSAL : "alert",
    success: "alert",
    processing: "info",
    failed: "error",

  };
  return BadgeStatus[status] || "default";
};

export const getColorsBillsStatus = (status: string) => {
  const BillsStatus = {
    Aprovado: "blueBills",
    Pagamentoagendado: "blueBills",
    Aprovar: "yellowBills",
    Reprovado: "orangeBills",
    Atualizaçãonecessária: "yellowBills",
    Erronopaamento: "redBills",
    Saldoinsulficiente: "orangeBills",
    Paga: "greenBills",
    Cancelada: "orangeBills",

  }
  return BillsStatus[status] || "black"
}

export const getPaymentMethod = (type: string) => {
  const PaymentMethod = {
    CREDIT_CARD: "Cartão de Crédito",
    CREDIT_CARD_EARLY_SELLER: "Cartão de Crédito",
    CREDIT_CARD_EARLY_BUYER: "Cartão de Crédito",
    CREDIT_CARD_EARLY_MIXED: "CREDIT_CARD_EARLY_MIXED",
    BANK_SLIP: "Boleto",
    PIX: "Pix",
  };
  return PaymentMethod[type] || type;
};

export const getInvoiceStatus = (status: string) => {
  const Status = {
    AUTHORIZED: "Emitida",
    FAILED: "Erro",
    NOT_EMITTED: "Não Emitida",
    PROCESS_CANCEL: "Cancelamento em processo",
    CANCELED: "Cancelada",
    SEND_INVOICE_SYSTEM: "Emissão em processamento",
    SEND_AUTHORIZED: "Emissão em processamento",
    PROCESS_AUTHORIZED: "Emissão em processamento",
  };

  return Status[status] || "Não Emitida";
};

export const getOperationMethod = (type?: string) => {
  const OperationMethod = {
    DEBIT_TRANSFER: "Repasse",
    INSTALLMENTS_ANTICIPATION: "Parcela de antecipação",
    DEBIT_CORRECTION: "Correção",
    CREDIT_CORRECTION: "Correção",
    FEE_DISCOUNT: "Desconto em taxas",
    REVENUE_ANTICIPATION: "Antecipação de receita",
  };
  return OperationMethod[type] || type;
};

export const getInitials = (value: string) => {
  if (value === "") {
    return "-";
  }
  const initials = value
    .split(" ")
    .map((w) => w[0])
    .join("")
    .substring(0, 2);
  return initials;
};

export function capitalizeFirstLetter(str: string) {
  if(str === "") return
  return str?.charAt(0)?.toUpperCase() + str?.slice(1);
}

export function formatWhiteLabelNames(str) {
  const whitelabels = {
    parcelamais: "Parcela mais",
    azp: "AZ Pague",
    flipsaude: "Quero flip",
    appbrlcard: "Brl",
    bsbpaycredit: "BSB Pay",
    bmhealthinvest: "BM Invest",
    stanleysveins: "Stanley's Veins",
    marlimco: "Marlim"
  }
  return whitelabels[str] || capitalizeFirstLetter(str);
}

export function formatPixKeyPlaceholders(str) {
  const pixKeyPlaceholders = {
    EMAIL: "nome@empresa.com.br",
    PHONE: "11 99999 9999",
    DOCUMENT: "01.123.456/0001-99",
    ALLEATORY_KEY: "",
  }
  return pixKeyPlaceholders[str] || "Digite sua chave pix.";
}

export function formatPixKeyMasks(str) {
  const pixKeyMasks = {
    EMAIL: "",
    ALLEATORY_KEY: "",
    PHONE: "cellphone",
    DOCUMENT: "cnpj",
  }
  return pixKeyMasks[str] || "";
}

export function formatCep(value: string) {
  value = value.replace(/\D/g, "");
  value = value.replace(/^(\d{5})(\d)/, "$1-$2");
  return value;
}

export function formatFinalNumbers(value: string) {
  if (!value) return "";
  if (value?.length < 4) return value;
  const ultimosQuatro = value?.slice(-4);
  return ultimosQuatro;
}

export function formatDateAndHour(isoString: string) {
  if(isoString?.includes("0000-00-00 00:00:00")) {
    const formattedDate = "00/00/0000";
    const formattedTime = "00:00:00";
    return { formattedDate, formattedTime }
  }
  if(isoString.includes("T")) {
    const date = parseISO(isoString?.split("T")[0]);
    const formattedDate = format(date, "dd/MM/yy", { locale: ptBR });
    const formattedTime = format(date, "HH:mm:ss");
    return { formattedDate, formattedTime };
  }
  if(isoString.includes("[")) {
    const date = parseISO(isoString?.split("[")[0]);
    const formattedDate = format(date, "dd/MM/yy", { locale: ptBR });
    const formattedTime = format(date, "HH:mm:ss");
    return { formattedDate, formattedTime };
  }
  if(isoString.includes(" ")) {
    const date = parseISO(isoString?.split(" ")[0]);
    const formattedDate = format(date, "dd/MM/yy", { locale: ptBR });
    const formattedTime = format(date, "HH:mm:ss");
    return { formattedDate, formattedTime };
  }
  
};

export function convertData(dataString: string) {
  const partes = dataString.split('/');
  if (partes.length === 3) {
    return `${partes[2]}-${partes[1]}-${partes[0]}`;
  }
  return null;
}

export function formatCardStatus(status: string) {
  const cardStatus = {
    PENDING: "Pendente",
    ACTIVE: "Desbloqueado",
    BLOCKED: "Bloqueado",
  }
  return cardStatus[status] || "";
}

export function formatCardType(type: string) {
  const cardType = {
    FREE_BALANCE: "Saldo livre",
    LIMIT_BALANCE: "Limite fixo",
  }
  return cardType[type] || "";
}


export const formatKycAnalysisResult = (result: string): Record<string, string[]> => {
  if (!result) return {};

  try {
    const cleanResult = result.replace(/[{}]/g, '').trim();
    const match = cleanResult.match(/(.+?)=\[(.*?)\]/);
    if (!match) return {};
    const [, key, message] = match;
    return {
      [key]: [message]
    };
  } catch (error) {
    console.error('Erro ao formatar resultado:', error);
    return {};
  }
};

export function formatPhoneE164(phone: string): string {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (phone.startsWith('+')) {
    return phone;
  }

  return `+${cleaned}`;
}

export function splitFullName(fullName: string): { firstName: string; lastName: string } {
  if (!fullName) return { firstName: '', lastName: '' };
  const [firstName, ...lastNameParts] = fullName.trim().split(' ');
  const lastName = lastNameParts.join(' ');
  return { firstName, lastName };
}

export const validateBarcode = (barcode: string): boolean => {
  // Remove espaços e caracteres especiais
  const cleanBarcode = barcode.replace(/\D/g, "");
  // Linha digitável tem 47 ou 48 dígitos
  if (cleanBarcode.length === 47 || cleanBarcode.length === 48) {
    return true;
  }

  // Código de barras tem 44 dígitos
  if (cleanBarcode.length === 44) {
    return true;
  }
  return false;
};

export function omitProp<T extends object>(obj: T, keys: (keyof T)[]): Partial<T> {
  const entries = Object.entries(obj).filter(([key]) =>
    !keys.includes(key as keyof T)
  ) as [keyof T, any][]; 

  return Object.fromEntries(entries) as Partial<T>;
}

export function handleErrors(error: any) {
  error?.response?.data?.errors?.forEach((err: any) => {
    showToast({
      type: "error",
      message: `${err?.code} - ${err?.description}`,
    });
  });
}

export function formatYearMonth(ym: string) {
  const date = parse(ym, "yyyy-MM", new Date());
  return format(date, "MMMM yyyy", { locale: ptBR }).replace(
    /^./,
    (s) => s.toUpperCase()
  );
}

// Formata o texto completo
export function formatPixKey(key: string, type: string) {
  if (!key) return "";

  const clean = key.replace(/\D/g, "");

  switch (type) {
    case "CPF":
      if (clean.length === 11) {
        return clean
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
      }
      return key;

    case "CNPJ": {
      if (hasCnpjShape(key)) {
        return formatCnpjString(key);
      }
      return key;
    }

    case "EMAIL":
      return key.toLowerCase();

    case "PHONE":
      // opcional, se vier telefone
      return key.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");

    case "RANDOM":
      // mostra só o início e fim da chave aleatória
      return `${key.slice(0, 6)}...${key.slice(-4)}`;

    default:
      return key;
  }
}



function parseMaybeJSONLoose(value: unknown): unknown {
  if (typeof value !== "string") return value;

  let s = value.trim();

  s = s.replace(/\\n/g, " ");
  if (s === "null") return null;
  if (s === "true") return true;
  if (s === "false") return false;

  if (/^-?\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (!Number.isNaN(n)) return n;
  }

  try {
    const first = JSON.parse(s);
    if (typeof first === "string") {
      try {
        return JSON.parse(first);
      } catch {
        return first;
      }
    }
    return first;
  } catch {
    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
      return s.slice(1, -1);
    }
    return s;
  }
}

export function normalizeGatewayResponses(input: string | unknown): NormalizeResult {
  const out: GatewayItem[] = [];
  const errs: NormalizeResult["errors"] = [];

  let arr: unknown;
  if (typeof input === "string") {
    try {
      arr = JSON.parse(input);
    } catch (e) {
      return {
        data: [],
        errors: [{ message: `JSON raiz inválido: ${(e as Error).message}`, raw: input }],
      };
    }
  } else {
    arr = input;
  }

  if (!Array.isArray(arr)) {
    return {
      data: [],
      errors: [{ message: "Esperado um array na raiz.", raw: arr }],
    };
  }
  arr.forEach((rawItem, index) => {
    const item: GatewayItem = { ...(rawItem as object) } as GatewayItem;
    if (item.fraudAnalysis === "null") item.fraudAnalysis = null;
    if (typeof item.error === "string") {
      const parsed = parseMaybeJSONLoose(item.error);
      if (typeof parsed === "string") {
        errs.push({
          index,
          message: "Campo 'error' permaneceu string após tentativa de parse.",
          raw: parsed,
        });
        item.error = parsed;
      } else {
        item.error = parsed;
      }
    }
    if (!item.provider || !item.status) {
      errs.push({
        index,
        message: "Item sem 'provider' ou 'status'.",
        raw: rawItem,
      });
    }

    out.push(item);
  });

  return { data: out, errors: errs };
}
